import { useState, useCallback } from "react";
import { conversationsApi } from "../API/conversationsApi";

export const useConversationsApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [conversations, setConversations] = useState([]);
const [unreadCount, setUnreadCount] = useState(0);
  const user = JSON.parse(localStorage.getItem("user")) ?? -1;





  const fetchUnreadCount = useCallback(async (conversationId) => {
    try {
      const result = await conversationsApi.getUnreadCount(conversationId);

      if (result.error) {
        console.log("Unread count error:", result.error);
        setUnreadCount(0);
        return 0;
      }

      setUnreadCount(result.data);
      return result.data;

    } catch (err) {
      console.error("Error fetching unread count:", err);
      setUnreadCount(0);
      return 0;
    }
  }, []);

  // Fetch all operator conversations (unread + in-progress)
  const fetchOperatorConversations = useCallback(async (operatorId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await conversationsApi.getOperatorConversations(
        operatorId
      );
      if (result.error) {
        setError(result.error);
        setConversations([]);
        return null;
      }
      setConversations(result);
      return result;
    } catch (err) {
      setError("Failed to fetch operator conversations");
      setConversations([]);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshOperatorConversations = useCallback(async () => {
    await fetchOperatorConversations(user.id);
  }, [fetchOperatorConversations]);

  // Get a conversation by ID (optionally with messages)
  const getConversation = useCallback(async (id, includeMessages = false) => {
    setLoading(true);
    setError(null);
    try {
      const result = await conversationsApi.getConversation(
        id,
        includeMessages
      );
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError("Failed to get conversation");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get client conversations (filter by status or operator)
  const getClientConversations = useCallback(
    async (clientId, status = null, operatorId = null) => {
      setLoading(true);
      setError(null);
      try {
        const result = await conversationsApi.getClientConversations(
          clientId,
          status,
          operatorId
        );
        if (result.error) {
          setError(result.error);
          return null;
        }
        return result;
      } catch (err) {
        setError("Failed to get client conversations");
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Create a conversation
  const createConversation = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await conversationsApi.createConversation(data);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError("Failed to create conversation");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get or create a conversation
  const getOrCreateConversation = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await conversationsApi.getOrCreateConversation(data);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError("Failed to get or create conversation");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Lock a conversation
  const lockConversation = useCallback(async (conversationId, data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await conversationsApi.lockConversation(
        conversationId,
        data
      );
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError("Failed to lock conversation");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Unlock a conversation
  const unlockConversation = useCallback(async (conversationId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await conversationsApi.unlockConversation(conversationId);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError("Failed to unlock conversation");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get lock settings for timer configuration
  const getLockSettings = useCallback(async () => {
    try {
      const result = await conversationsApi.getLockSettings();
      if (result.error) {
        // Return default if API fails
        return { lockDurationMinutes: 5, lockDurationMs: 5 * 60 * 1000 };
      }
      return result.data || result;
    } catch (err) {
      // Return default on error
      return { lockDurationMinutes: 5, lockDurationMs: 5 * 60 * 1000 };
    }
  }, []);

  return {
    loading,
    error,
    conversations,
    unreadCount,
    fetchUnreadCount,
    fetchOperatorConversations,
    refreshOperatorConversations,
    getConversation,
    getClientConversations,
    createConversation,
    getOrCreateConversation,
    lockConversation,
    unlockConversation,
    getLockSettings,
  };
};
